/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
  useCallback,
  useState,
} from "react"
import { type AnalysisType } from "@/generated/graphql"
import CopilotTooltip from "@/components/analysis/CopilotTooltip"
import { CopilotProvider, type TooltipProps } from "react-native-copilot"
import EmptyView from "@/components/EmptyView"
import { BFColors } from "@/constants/Colors"
import { Platform } from "react-native"
import { useStore } from "@nanostores/react"
import { $subjectPropertyWalkthroughDismissed } from "@/stores/walkThroughs"

interface AnalyzeContextProps {
  analysisId: string
  onCustomCompsPress: () => void
  onSortCompsPress: () => void
  onFindCompsPress: (placeId?: string) => void
  isFirstManualAnalysisDisplay: boolean
  isSelectForMeChosen: boolean
  setIsSelectForMeChosen: (value: boolean) => void
  isTourActive: boolean
  setIsTourActive: (isActive: boolean) => void
  showBackflipSuggestedComps: boolean
}

const defaultContextValue: AnalyzeContextProps = {
  analysisId: "",
  onCustomCompsPress: () => {},
  onSortCompsPress: () => {},
  onFindCompsPress: () => {}, // Default function does not use placeId
  isFirstManualAnalysisDisplay: false,
  isSelectForMeChosen: false,
  setIsSelectForMeChosen: () => {},
  isTourActive: false,
  setIsTourActive: () => {},
  showBackflipSuggestedComps: true,
}

export const AnalyzeContext =
  createContext<AnalyzeContextProps>(defaultContextValue)

export const useAnalyzeContext = (): AnalyzeContextProps => {
  const context = useContext(AnalyzeContext)
  if (!context) {
    throw new Error("useAnalyzeContext must be used within an AnalyzeProvider")
  }
  return context
}

interface AnalyzeProviderProps {
  children: ReactNode
  analysisId: string
  analysis?: AnalysisType | null
  onCustomCompsPress: () => void
  onSortCompsPress: () => void
  onFindCompsPress: (placeId?: string) => void
  isTourActive?: boolean
  setIsTourActive?: (isActive: boolean) => void
}

export const AnalyzeProvider = ({
  children,
  analysisId,
  analysis,
  onCustomCompsPress,
  onSortCompsPress,
  onFindCompsPress,
  isTourActive: externalIsTourActive,
  setIsTourActive: externalSetIsTourActive,
}: AnalyzeProviderProps) => {
  const {
    isFirstManualAnalysisDisplay,
    isSelectForMeChosen,
    setIsSelectForMeChosen,
    showBackflipSuggestedComps,
  } = useBetaTesting({
    analysis,
  })

  const [isTourActive, setIsTourActiveState] = useState(false)

  const setIsTourActive = useCallback((isActive: boolean) => {
    setIsTourActiveState(isActive)
  }, [])

  const value = useMemo(
    () => ({
      analysisId,
      onCustomCompsPress,
      onSortCompsPress,
      onFindCompsPress,
      isFirstManualAnalysisDisplay,
      isSelectForMeChosen,
      setIsSelectForMeChosen,
      isTourActive,
      setIsTourActive,
      showBackflipSuggestedComps,
    }),
    [
      analysisId,
      onCustomCompsPress,
      onSortCompsPress,
      onFindCompsPress,
      isFirstManualAnalysisDisplay,
      isSelectForMeChosen,
      setIsSelectForMeChosen,
      setIsTourActive,
      isTourActive,
      showBackflipSuggestedComps,
    ]
  )

  return (
    <AnalyzeContext.Provider value={value}>
      <CopilotProvider
        tooltipComponent={CopilotTooltip as React.ComponentType<TooltipProps>}
        stepNumberComponent={EmptyView}
        overlay={Platform.OS === "web" ? "view" : "svg"}
        animated={true}
        backdropColor={BFColors.darkest11}
        androidStatusBarVisible={false}
        stopOnOutsideClick={true}
        tooltipStyle={{
          backgroundColor: BFColors.lightest,
        }}
        arrowColor={BFColors.lightest}
        svgMaskPath={tourSvgMaskPath}
      >
        {children}
      </CopilotProvider>
    </AnalyzeContext.Provider>
  )
}

interface UseBetaTestingProps {
  analysis?: AnalysisType | null
}

const useBetaTesting = ({ analysis }: UseBetaTestingProps) => {
  const shouldDisplayWalkthrough = () => {
    const walkthroughState = useStore($subjectPropertyWalkthroughDismissed)

    if (walkthroughState === null) {
      return false
    }

    return !walkthroughState
  }

  const [isSelectForMeChosen, setIsSelectForMeChosen] = useState(false)

  // Temp Beta User flag check
  // For testing...
  // const isFirstManualAnalysisDisplay = !isSelectForMeChosen // force true for testing

  const isFirstManualAnalysisDisplay =
    (shouldDisplayWalkthrough() && !isSelectForMeChosen) ?? false
  const showBackflipSuggestedComps = useMemo(() => {
    if (!isFirstManualAnalysisDisplay) return true

    if (isSelectForMeChosen) {
      return true
    }
    return false
  }, [isFirstManualAnalysisDisplay, isSelectForMeChosen])

  return {
    isFirstManualAnalysisDisplay,
    analysis,
    isSelectForMeChosen,
    setIsSelectForMeChosen,
    showBackflipSuggestedComps,
  }
}

// @ts-expect-error Typing this function breaks the functionality and Copilot library's types are kept local
const tourSvgMaskPath = ({ position, size, canvasSize }) => {
  const padding = -2

  const adjustedWidth = Math.max(0, size.x._value * 2)
  const adjustedHeight = Math.max(0, size.y._value + padding * 2)

  const x = position.x._value
  const y = position.y._value - padding

  const safeX = Math.min(Math.max(0, x), canvasSize.x)
  const safeY = Math.min(Math.max(0, y), canvasSize.y)

  return `M0,0H${canvasSize.x}V${canvasSize.y}H0V0Z M${safeX},${safeY}h${adjustedWidth}v${adjustedHeight}h-${adjustedWidth}Z`
}
